import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { TransmissionService } from 'src/app/services/transmission/transmission.service';
import { TransmissionModalService } from 'src/app/services/transmission-modal/transmission-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-transmission',
  templateUrl: './add-edit-transmission.component.html',
  styleUrls: ['./add-edit-transmission.component.scss']
})
export class AddEditTransmissionComponent extends DestructorComponent implements OnInit {

  @Output() transmissionAdded = new EventEmitter<void>();
  @Output() transmissionUpdated = new EventEmitter<void>();

  transmissionForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedTransmission: any = null;

  constructor(
    private fb: FormBuilder,
    private _transmissionService: TransmissionService,
    private _transmissionModalService: TransmissionModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.transmissionForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  // Getter methods for form controls
  get name() { return this.transmissionForm.get('name'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_transmission_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceTransmission = this._transmissionModalService.getSelectedTransmission();
        if (serviceTransmission) {
          this.isEditMode = true;
          this.selectedTransmission = serviceTransmission;
          this.populateForm(this.selectedTransmission);
        } else {
          this.isEditMode = false;
          this.selectedTransmission = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._transmissionModalService.clearSelectedTransmission();
        this.resetForm();
      });
    }
  }

  private populateForm(transmission: any): void {
    this.transmissionForm.patchValue({
      name: transmission.name || ''
    });
  }

  private resetForm(): void {
    this.transmissionForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.transmissionForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_transmission_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.transmissionForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.transmissionForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._transmissionService.UpdateData(this.selectedTransmission._id, submitData)
        : this._transmissionService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Transmission operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Transmission has been successfully updated!"
                : "Transmission has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.transmissionUpdated.emit();
                } else {
                  this.transmissionAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Transmission operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.transmissionForm);
    }
  }
}
